import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import ChatEmbed from '../UseCaseComponents/ChatEmbed';
import { site } from '../../configs/site';

const ChatStarterLayout = ({ useCase }) => {
  const { chat } = useCase.components;
  const builtWith = useCase.builtWith || [];
  const [isChatLoading, setIsChatLoading] = useState(true);

  return (
    <div className="mt-8 w-full">
      {/* Framed chat window. max-w + mx-auto keep it a sensible chat width
          instead of stretching edge-to-edge on wide desktop screens. */}
      <div className="mx-auto w-full max-w-[720px] overflow-hidden rounded-2xl border-2 border-primary-800 shadow-lg">
        <div className="flex items-center gap-2 bg-primary-800 px-3 py-3 sm:gap-3 sm:px-5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 sm:h-8 sm:w-8">
            <ChatBubbleLeftRightIcon className="h-4 w-4 text-white sm:h-5 sm:w-5" aria-hidden="true" />
          </span>
          {/* min-w-0 lets this shrink/truncate instead of forcing the row to
              overflow past the viewport on narrow phones (e.g. iPhone SE) */}
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white sm:text-base">
            West Loop Strategy Assistant
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium text-white/70">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                isChatLoading ? 'animate-pulse bg-yellow-400' : 'bg-green-400'
              }`}
              aria-hidden="true"
            />
            {isChatLoading ? 'Connecting...' : 'Online'}
          </span>
        </div>

        <div className="bg-white">
          {/* Was a dvh-based clamp() that grew with the viewport -- even
              scaled down twice, it kept landing taller than the chat
              actually needs and leaving empty space below the last
              message. Fixed height instead (a smaller one below the sm
              breakpoint, since a flat 600px would be too tall relative to
              a phone screen): this is roughly what a default-size laptop
              browser window shows, so it no longer grows just because the
              window/screen is taller. */}
          <ChatEmbed
            agentId={chat.agentId}
            className="h-[450px] sm:h-[600px] w-full"
            onLoadingChange={setIsChatLoading}
          />
        </div>
      </div>

      {/* Info tiles */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {useCase.about && (
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-gray-900">
              What this demo shows
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {useCase.about}
            </p>
          </div>
        )}

        {builtWith.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-gray-900">
              Built with
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {builtWith.map((tool) => (
                <span
                  key={tool}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700"
                >
                  <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {tool}
                </span>
              ))}
            </div>
          </div>
        )}

        {site.contact.enabled && (
          <div className="flex flex-col gap-3 rounded-xl bg-primary-600 p-5">
            <h3 className="text-sm font-semibold text-white">
              Want this for your team?
            </h3>
            <p className="text-sm text-white/80">
              {site.contact.description}
            </p>
            <Link
              to="/contact"
              className="mt-auto inline-flex w-fit items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primary-700 shadow-sm hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              {site.contact.buttonLabel}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatStarterLayout;
